import { Box, styled } from '@mui/material';
import { useCallback } from 'react';

import {
  CASE_STATUS_INDEX,
  type ENOPCaseStatus,
  type NewCase,
  NewCaseCard,
  useDetectUpdatedCaseFields,
} from '#entities';
import {
  ELayoutManagerMode,
  Loader,
  useCardLayout,
  useNewItemDetector,
} from '#shared';

import { Carousel, CarouselStatic } from '../../Carousel';
import { CaseDetailsDrawer, useCaseSelection } from '../../CaseDrawer';
import { useAppBehavior, useData } from '../../data-context';
import { useLayoutMode } from '../../LayoutManager';
import { relationsSelectors } from '../../slices';
import { useWidgetItemsDistribution } from '../../WidgetDistribution';

import type { WidgetProps } from '../../widgetManager';

const newCaseFieldsToCompare: (keyof NewCase)[] = [
  'label',
  'organ',
  'donorHospital',
  'transplantCenterCode',
  'oscStatus',
];

export interface NewCaseCardsProps {
  instance: WidgetProps['instance'];
  caseStatuses?: ENOPCaseStatus[];
  sortBy?:
    | keyof Pick<
        NewCase,
        'id' | 'donorType' | 'label' | 'oscStatus' | 'donorORTimeString'
      >
    | 'oscStatusSequence';
  maxRows?: number;
  highlightDuration?: number;
  maxHeight?: number;
  variant?: 'horizontal' | 'vertical';
  side?: 'left' | 'right';
}

const CARD_MIN_WIDTH = 140;
const CARD_MAX_WIDTH = 300;
const CARD_MIN_HEIGHT = 160;
const GAP = 8;

const PREFIX = 'TSMED-NewCaseCards';

const Root = styled(Box, {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  height: '100%',
});

/**
 * NewCaseCard component for displaying new case alerts.
 * Follows the project's card design patterns and performance guidelines.
 *
 * **Variant Behavior:**
 * - 'horizontal': Used for standalone widgets in sections
 * - 'vertical': Used for aside widgets next to the geo map
 *
 * Note: The variant is typically auto-detected by WidgetNewCases based on
 * instance.instanceIndex. Manual override is possible but not recommended.
 */
export const NewCaseCards = ({
  instance,
  caseStatuses = [],
  sortBy = 'donorORTimeString',
  maxRows = 0,
  highlightDuration = 10_000,
  maxHeight = 0,
  variant = 'horizontal',
  side,
}: NewCaseCardsProps) => {
  const { filteredNewCases: newCases } = useData();
  const { updatedCases } = useDetectUpdatedCaseFields(newCases, {
    exactFieldsOnly: newCaseFieldsToCompare,
  });
  const { isNew } = useNewItemDetector(newCases, item => item.id);
  const { onCaseLoopNext } = useAppBehavior();
  const {
    open: isCaseDrawerOpen,
    selectedCase,
    handleClose: handleCaseDrawerClose,
    selectedCaseType,
    handleSelectCaseById,
  } = useCaseSelection();
  const geoMapCenter = relationsSelectors.useSelectGeoMapCenter();
  const { mode } = useLayoutMode();
  const isPreviewMode = mode !== ELayoutManagerMode.RENDER;

  let list: NewCase[] = [];
  if (caseStatuses.length > 0) {
    list = newCases.filter(item => caseStatuses.includes(item.oscStatus));
  } else {
    list = newCases;
  }
  if (sortBy) {
    if (sortBy === 'oscStatusSequence') {
      list = [...list].sort(
        (a, b) =>
          CASE_STATUS_INDEX[a.oscStatus] - CASE_STATUS_INDEX[b.oscStatus],
      );
    } else {
      list = [...list].sort((a, b) =>
        (a?.[sortBy] ?? 0) > (b?.[sortBy] ?? 0) ? -1 : 1,
      );
    }
  }

  const items: Array<NewCase & { updatedFields: string[] }> = list.map(c => ({
    ...c,
    updatedFields: updatedCases[c.id] ?? [],
    isNewOnBoard: isNew(c.id),
  }));

  const availableRowsCount = Math.floor(
    (maxHeight + GAP) / (CARD_MIN_HEIGHT + GAP),
  );

  const safeMaxHeight =
    Number.isFinite(maxHeight) && maxHeight > 0 ? maxHeight : 0;

  // Calculate max items per side (at least 1 per row)
  const maxItemsPerSide =
    safeMaxHeight > 0
      ? Math.max(1, Math.floor((safeMaxHeight + GAP) / (CARD_MIN_HEIGHT + GAP)))
      : 1;

  const { items: distributed } = useWidgetItemsDistribution(
    items,
    instance,
    variant,
    maxItemsPerSide,
  );

  const { itemWidth, rowsCount, handleResize } = useCardLayout({
    itemsCount: distributed?.length ?? 0,
    maxRows,
    availableHeight: safeMaxHeight,
    maxCardWidth: CARD_MAX_WIDTH,
    minCardWidth: CARD_MIN_WIDTH,
    gap: GAP,
  });

  const handleCardClick = useCallback(
    (id: string) => handleSelectCaseById(id),
    [handleSelectCaseById],
  );

  const vertical = variant === 'vertical';
  const isGeoMapCenterSet = geoMapCenter[0] !== 0 || geoMapCenter[1] !== 0;
  const itemWidthFinal = vertical ? 150 : itemWidth;

  // final items to render:
  const itemsToRender = distributed;
  const normalSize = vertical ? Math.max(itemsToRender.length, 1) : rowsCount;

  // Show loader until geo map center is set for vertical layout
  // Skip this check in preview mode (VIEW/EDIT) since geo map renders as static image
  if (vertical && !isPreviewMode && !isGeoMapCenterSet) {
    return <Loader />;
  }

  const itemProps = {
    highlightDuration,
    width: itemWidthFinal,
    onClick: handleCardClick,
    side,
  };

  const CarouselComponent = isPreviewMode ? CarouselStatic : Carousel;

  return availableRowsCount > 0 ? (
    <Root justifyContent={vertical ? 'center' : 'flex-start'}>
      <CarouselComponent
        renderItem={data => <NewCaseCard data={data} {...itemProps} />}
        items={itemsToRender}
        itemKey="id"
        normalSize={normalSize}
        gap={GAP}
        onResize={handleResize}
        itemWidth={itemWidthFinal}
        itemHeight={CARD_MIN_HEIGHT}
        onLoopNext={isPreviewMode ? undefined : onCaseLoopNext}
        variant={variant}
      />

      <CaseDetailsDrawer
        open={isCaseDrawerOpen}
        onClose={handleCaseDrawerClose}
        caseData={selectedCase}
        selectedCaseType={selectedCaseType}
      />
    </Root>
  ) : null;
};

NewCaseCards.displayName = 'NewCaseCards';
